import dayjs from 'dayjs';
import { BuildingSelectionModal, SpaceHierarchySelectionModal } from '..';
import { useDashboardFilters } from '../../context/dashboard-filters.context';
import { CardTitle } from '@client/toolkit';
import { useTranslation } from 'react-i18next';
import { ActiveFilters } from '../../../../components/ui/active-filters/active-filters';

import { DateRangePresetKey, useDefaultDateRangePresets } from './useDefaultDateRangePresets';
import { DatePresetButton } from './date-preset-button';
import 'dayjs/locale/nb';
import DateRangePickerBasic from '../../../reporting/components/date-range-picker-with-shortcuts/date-range-picker-basic';
import { useGetBuildingsQuery } from '@client/api';
import { useEffect, useMemo } from 'react';

// Backend PackageType enum values (as strings for JSON serialization)
enum BackendPackageType {
  Management = 'Management',
  Eos = 'Eos',
}

export interface DashboardFiltersProps {
  showOnlySpacesWithEosEnabled?: boolean;
  showOnlySpacesWithManagementEnabled?: boolean;
  visiblePresets?: DateRangePresetKey[];
  defaultPresetKey?: DateRangePresetKey;
  useHierarchicalSpaceSelection?: boolean;
  selectMultipleSpaces?: boolean;
}

export const DashboardFilters = ({
  showOnlySpacesWithEosEnabled,
  showOnlySpacesWithManagementEnabled,
  visiblePresets,
  defaultPresetKey = DateRangePresetKey.LAST30DAYS,
  useHierarchicalSpaceSelection = false,
  selectMultipleSpaces = true,
}: DashboardFiltersProps) => {
  const {
    selectedBuildings,
    setSelectedBuildings,
    selectedSpaces,
    setSelectedSpaces,
    selectedDateFrom,
    setSelectedDateFrom,
    selectedDateTo,
    setSelectedDateTo,
    setActivePresetKey,
  } = useDashboardFilters();
  const { t } = useTranslation('dashboard');

  const { presets, activePreset, setActivePreset } = useDefaultDateRangePresets({
    defaultPresetKey,
    visiblePresets,
    initialDateFrom: selectedDateFrom,
    initialDateTo: selectedDateTo,
    onChange: (preset) => {
      setSelectedDateFrom(preset.from);
      setSelectedDateTo(preset.to);
    },
  });

  useEffect(() => {
    setActivePresetKey(activePreset?.key ?? null);
  }, [activePreset?.key, setActivePresetKey]);

  const packageFilter = useMemo(() => {
    if (showOnlySpacesWithEosEnabled) {
      return BackendPackageType.Eos;
    }
    if (showOnlySpacesWithManagementEnabled) {
      return BackendPackageType.Management;
    }
    return undefined;
  }, [showOnlySpacesWithEosEnabled, showOnlySpacesWithManagementEnabled]);

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full max-w-[15em]">
        {useHierarchicalSpaceSelection ? (
          <SpaceHierarchySelectionModal
            selectedSpaces={selectedSpaces}
            packageFilter={packageFilter}
            selectMultipleSpaces={selectMultipleSpaces}
            onChange={(value) => {
              setSelectedSpaces(value);
            }}
          />
        ) : (
          <BuildingSelectionModal
            selectedBuilding={selectedBuildings}
            packageFilter={packageFilter}
            onChange={(value) => {
              setSelectedBuildings(value);
            }}
          />
        )}
      </div>
      <ActiveFilters
        active={
          useHierarchicalSpaceSelection
            ? selectedSpaces.map((space) => ({
                id: space.id,
                label: (space as any).displayPath ?? space.name ?? '',
              }))
            : selectedBuildings.map((building) => ({ id: building.id, label: building.name }))
        }
        onRemove={(id) =>
          useHierarchicalSpaceSelection
            ? setSelectedSpaces(selectedSpaces.filter((s) => s.id !== id))
            : setSelectedBuildings(selectedBuildings.filter((b) => b.id !== id))
        }
      />
      <div className="flex flex-col gap-2">
        <CardTitle className="flex-1" variant="dense">
          {t('timePeriod')}
        </CardTitle>
        <div>
          <div className="inline-flex flex-wrap gap-2">
            {presets.map((preset) => (
              <DatePresetButton
                key={preset.key}
                label={preset.label}
                isActive={activePreset?.key === preset.key}
                onClick={() => setActivePreset(preset)}
              />
            ))}
          </div>
        </div>
      </div>
      {activePreset?.key === DateRangePresetKey.CUSTOM && (
        <div className="flex flex-wrap gap-2">
          <DateRangePickerBasic
            selectedDate={[dayjs(selectedDateFrom), dayjs(selectedDateTo)]}
            onChange={(value) => {
              if (value && value[0] && value[1]) {
                setSelectedDateFrom(value[0]);
                setSelectedDateTo(value[1]);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};
